import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Upload, FileUp, Loader2, CheckCircle2, AlertTriangle, XCircle, History, RotateCcw } from "lucide-react";

const fmtDmy = (iso) => (iso && iso.length === 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso || "—");

const OPTION_DEFS = [
  ["apply_judges", "Δικαστής Προδικαστικού"],
  ["apply_acts", "Αρ. Πράξης Ορισμού"],
  ["apply_bench", "Δικαστής Έδρας (κεφαλίδα)"],
  ["apply_notes", "Σημειώσεις («ΧΩΡΙΣ ΠΡΟΤΑΣΕΙΣ», «ΠΑΡΑΠΟΜΠΗ»)"],
  ["apply_pinakio", "Διόρθωση πινακίου / σήμανσης (ΜΟΝ)/(ΠΟΛ) / δικασίμου / τύπου"],
  ["create_judges", "Προσθήκη νέων δικαστών στο μητρώο (χωρίς βαθμό)"],
];

const ChangeCell = ({ row, field }) => {
  const ch = (row.changes || []).find((c) => c.field === field || (field === "assignment_act" && c.field === "assignment_act"));
  const cur = field === "assigned_judge" ? row.current?.assigned_judge : field === "assignment_act" ? row.current?.assignment_act : row.current?.bench_judge;
  if (!row.found) return <span className="text-slate-300">—</span>;
  if (!ch) return <span className="text-slate-600">{cur || <span className="text-slate-300">—</span>}</span>;
  return (
    <span className="inline-flex flex-col leading-tight">
      {ch.from ? <span className="text-[11px] text-slate-400 line-through">{ch.from}</span> : null}
      <span className="font-medium text-emerald-700">{ch.to}</span>
    </span>
  );
};

const BulletinImportTab = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [opts, setOpts] = useState(Object.fromEntries(OPTION_DEFS.map(([k]) => [k, true])));
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/pretrial-tools/hearing-bulletin/import/history`);
      setHistory(r.data?.items || []);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const doPreview = async () => {
    if (!file) return toast.error("Επιλέξτε αρχείο Δελτίου (.docx / .doc / .pdf)");
    setUploading(true); setResult(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/pretrial-tools/hearing-bulletin/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setSelected(Object.fromEntries((r.data.rows || []).filter((x) => x.found && x.changes?.length).map((x) => [x.row_id, true])));
      toast.success(`Αναγνωρίστηκαν ${r.data.summary.total} γραμμές — βρέθηκαν ${r.data.summary.found} υποθέσεις`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ανάγνωσης Δελτίου", { duration: 10000 });
    } finally { setUploading(false); }
  };

  const doApply = async () => {
    setApplying(true);
    try {
      const row_ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      const r = await axios.post(`${API}/pretrial-tools/hearing-bulletin/import/apply`, { import_id: preview.id, row_ids, ...opts });
      setResult(r.data);
      setPreview((p) => ({ ...p, status: "applied" }));
      toast.success(r.data.message, { duration: 12000 });
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εφαρμογής", { duration: 10000 });
    } finally { setApplying(false); setConfirmOpen(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const h = preview?.header || {};
  const s = preview?.summary || {};

  return (
    <div className="space-y-4" data-testid="bulletin-import-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileUp className="w-5 h-5" /> Εισαγωγή Ενημερωτικού Δελτίου Δικασίμου</CardTitle>
          <CardDescription>
            Ανεβάστε το Δελτίο (.docx / .doc / .pdf με κείμενο) με συμπληρωμένες τις στήλες «Δικαστής» και «Αρ.Πράξης» (και «Δικαστής Έδρας» στην κεφαλίδα).
            Θα δείτε προεπισκόπηση ανά υπόθεση και θα επιβεβαιώσετε πριν την ενημέρωση. Το Δελτίο υπερισχύει των υπαρχόντων στοιχείων.
            Η ενέργεια καταγράφεται και μπορεί να αναιρεθεί από Διαχείριση → Ενέργειες.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".docx,.doc,.pdf" className="hidden" data-testid="bulletin-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="bulletin-choose-file-btn">
              <Upload className="w-4 h-4 mr-2" /> Επιλογή αρχείου
            </Button>
            <span className="text-sm text-slate-600 truncate max-w-[420px]" data-testid="bulletin-file-name">{file ? file.name : "Κανένα αρχείο"}</span>
            <Button onClick={doPreview} disabled={!file || uploading} data-testid="bulletin-preview-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />} Ανάγνωση & Προεπισκόπηση
            </Button>
          </div>
          <p className="text-xs text-slate-500">Σκαναρισμένα PDF (εικόνα) δεν διαβάζονται — ανεβάστε το .docx/.doc του Δελτίου.</p>
        </CardContent>
      </Card>

      {preview && (
        <Card data-testid="bulletin-preview-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              Προεπισκόπηση: <Badge variant="outline" data-testid="bulletin-header-court-type">{h.court_type || "—"}</Badge>
              <Badge variant="outline" data-testid="bulletin-header-date">Δικάσιμος {fmtDmy(h.hearing_date)}</Badge>
              {h.pinakio_label && <Badge className="bg-blue-600 text-white" data-testid="bulletin-header-pinakio">{h.pinakio_label}</Badge>}
              {h.pinakio_hint && !h.pinakio_label && <Badge variant="outline">{h.pinakio_hint}</Badge>}
              {h.bench_judge && <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50" data-testid="bulletin-header-bench">Δικαστής Έδρας: {h.bench_judge}</Badge>}
            </CardTitle>
            <CardDescription className="flex gap-3 flex-wrap text-xs" data-testid="bulletin-summary">
              <span>Γραμμές Δελτίου: <b>{s.total}</b>{s.declared_total != null && s.declared_total !== s.total ? <span className="text-red-600"> (δηλωμένο σύνολο {s.declared_total})</span> : null}</span>
              <span className="text-emerald-700">Βρέθηκαν: <b>{s.found}</b></span>
              {s.not_found > 0 && <span className="text-red-600">Δεν βρέθηκαν: <b>{s.not_found}</b></span>}
              <span>Με αλλαγές: <b>{s.with_changes}</b></span>
              {(preview.new_judges || []).length > 0 && (
                <span className="text-amber-700" data-testid="bulletin-new-judges">Νέοι δικαστές (θα προστεθούν στο μητρώο): <b>{preview.new_judges.join(", ")}</b></span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {OPTION_DEFS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={!!opts[k]} onCheckedChange={(v) => setOpts((o) => ({ ...o, [k]: !!v }))} data-testid={`bulletin-opt-${k}`} />
                  {label}
                </label>
              ))}
            </div>
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs" data-testid="bulletin-preview-table">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="p-2 text-center">
                      <Checkbox checked={selectedCount > 0 && selectedCount === preview.rows.filter((x) => x.found && x.changes?.length).length}
                        onCheckedChange={(v) => setSelected(Object.fromEntries(preview.rows.filter((x) => x.found && x.changes?.length).map((x) => [x.row_id, !!v])))}
                        data-testid="bulletin-select-all" />
                    </th>
                    <th className="p-2 text-left">α/α</th>
                    <th className="p-2 text-left">ΓΑΚ / Έτος</th>
                    <th className="p-2 text-left">ΕΑΚ</th>
                    <th className="p-2 text-left">Είδος</th>
                    <th className="p-2 text-left">Πινάκιο υπόθεσης</th>
                    <th className="p-2 text-left">Δικαστής</th>
                    <th className="p-2 text-left">Αρ. Πράξης</th>
                    <th className="p-2 text-left">Δικαστής Έδρας</th>
                    <th className="p-2 text-left">Κατάσταση</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const pin = (r.changes || []).find((c) => c.field === "pinakio");
                    const hasChanges = r.found && r.changes?.length > 0;
                    return (
                      <tr key={r.row_id} className={`border-t ${!r.found ? "bg-red-50" : hasChanges ? "" : "bg-slate-50 text-slate-400"}`} data-testid={`bulletin-row-${r.row_id}`}>
                        <td className="p-2 text-center">
                          {hasChanges && <Checkbox checked={!!selected[r.row_id]} onCheckedChange={(v) => setSelected((s0) => ({ ...s0, [r.row_id]: !!v }))} data-testid={`bulletin-row-select-${r.row_id}`} />}
                        </td>
                        <td className="p-2">{r.n}</td>
                        <td className="p-2 font-mono" data-testid={`bulletin-row-gak-${r.row_id}`}>{r.gak}/{r.year}</td>
                        <td className="p-2 font-mono text-blue-700">{r.eak || "—"}</td>
                        <td className="p-2">{r.kind || "—"}</td>
                        <td className="p-2 font-mono">
                          {pin ? (
                            <span className="inline-flex flex-col leading-tight">
                              <span className="text-[11px] text-slate-400 line-through">{pin.from || "(κενό)"}</span>
                              <span className="text-emerald-700">{pin.to}</span>
                            </span>
                          ) : (r.current?.pinakio || <span className="text-slate-300">—</span>)}
                        </td>
                        <td className="p-2" data-testid={`bulletin-row-judge-${r.row_id}`}>
                          {r.no_pretrial ? <Badge variant="outline" className="border-slate-300">{r.note || "ΧΩΡΙΣ ΠΡΟΔΙΚΑΣΤΙΚΟ"}</Badge> : <ChangeCell row={r} field="assigned_judge" />}
                          {!r.no_pretrial && r.note && <div className="text-[11px] text-amber-700">{r.note}</div>}
                        </td>
                        <td className="p-2" data-testid={`bulletin-row-act-${r.row_id}`}><ChangeCell row={r} field="assignment_act" /></td>
                        <td className="p-2"><ChangeCell row={r} field="bench_judge" /></td>
                        <td className="p-2">
                          {!r.found ? (
                            <span className="text-red-700 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Δεν βρέθηκε</span>
                          ) : hasChanges ? (
                            <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.changes.length} αλλαγ{r.changes.length === 1 ? "ή" : "ές"}</span>
                          ) : <span>Χωρίς αλλαγές</span>}
                          {(r.warnings || []).filter((w) => !w.startsWith("Η υπόθεση δεν βρέθηκε")).map((w, i) => (
                            <div key={i} className="text-[11px] text-amber-700 inline-flex items-start gap-1 mt-0.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}</div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={() => setConfirmOpen(true)} disabled={preview.status === "applied" || selectedCount === 0 || applying}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulletin-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Εφαρμογή σε {selectedCount} υποθέσεις
              </Button>
              {preview.status === "applied" && <Badge className="bg-emerald-600 text-white" data-testid="bulletin-applied-badge">Εφαρμόστηκε</Badge>}
              <Button variant="ghost" size="sm" onClick={() => { setPreview(null); setResult(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="bulletin-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Νέο αρχείο
              </Button>
            </div>
            {result && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="bulletin-result">
                <b>{result.message}</b>
                {result.created_judges?.length > 0 && <div className="text-xs mt-1">Νέοι στο μητρώο: {result.created_judges.join(", ")}</div>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Εφαρμογή Δελτίου Δικασίμου</AlertDialogTitle>
            <AlertDialogDescription>
              Θα ενημερωθούν <b>{selectedCount}</b> υποθέσεις ({h.court_type}, δικάσιμος {fmtDmy(h.hearing_date)}) σύμφωνα με το Δελτίο.
              Τα υπάρχοντα στοιχεία (Δικαστής, Αρ. Πράξης, Δικαστής Έδρας) αντικαθίστανται. Η ενέργεια μπορεί να αναιρεθεί από Διαχείριση → Ενέργειες.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulletin-confirm-cancel">Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={doApply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulletin-confirm-apply">Ναι, εφαρμογή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {history.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Πρόσφατες εισαγωγές</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-xs" data-testid="bulletin-history-table">
              <thead><tr className="bg-slate-100"><th className="p-2 text-left">Ημ/νία</th><th className="p-2 text-left">Αρχείο</th><th className="p-2 text-left">Δελτίο</th><th className="p-2 text-center">Γραμμές</th><th className="p-2 text-center">Ενημερώθηκαν</th><th className="p-2 text-left">Κατάσταση</th><th className="p-2 text-left">Χρήστης</th></tr></thead>
              <tbody>
                {history.map((it) => (
                  <tr key={it.id} className="border-t">
                    <td className="p-2 font-mono">{fmtDmy((it.created_at || "").slice(0, 10))} {(it.created_at || "").slice(11, 16)}</td>
                    <td className="p-2 truncate max-w-[260px]" title={it.filename}>{it.filename}</td>
                    <td className="p-2">{it.header?.court_type} {fmtDmy(it.header?.hearing_date)} {it.header?.pinakio_label || it.header?.pinakio_hint || ""}</td>
                    <td className="p-2 text-center">{it.summary?.found}/{it.summary?.total}</td>
                    <td className="p-2 text-center">{it.status === "applied" ? it.updated : "—"}</td>
                    <td className="p-2">{it.status === "applied" ? <Badge className="bg-emerald-600 text-white">Εφαρμόστηκε</Badge> : <Badge variant="outline">Προεπισκόπηση</Badge>}</td>
                    <td className="p-2">{it.applied_by || it.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulletinImportTab;
